import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Building2, Users, Check, X, Loader2, UserPlus, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const JoinPage = () => {
  const { token: inviteToken } = useParams();
  const { user, token: authToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInvitation();
  }, [inviteToken]);

  const fetchInvitation = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/by-token/${inviteToken}`);
      const data = await response.json();
      
      if (response.ok) {
        setInvitation(data);
      } else {
        setError(data.detail || 'Invalid or expired invitation');
      }
    } catch (err) {
      setError('Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Redirect to register with invite token
      navigate(`/register?invite=${inviteToken}`);
      return;
    }

    setAccepting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: inviteToken })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        // Navigate to the joined target
        if (data.target_type === 'workspace') {
          navigate('/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(data.detail || 'Failed to accept invitation');
      }
    } catch (err) {
      setError('Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-gray-400">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md w-full mx-4 text-center shadow-2xl">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Invitation Error
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline">Go to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isHub = invitation.target_type === 'hub';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md w-full shadow-2xl" data-testid="join-page">
        {/* Icon */}
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
          isHub ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
        }`}>
          {isHub ? (
            <Users className={`w-8 h-8 text-purple-600 dark:text-purple-400`} />
          ) : (
            <Building2 className={`w-8 h-8 text-blue-600 dark:text-blue-400`} />
          )}
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            You're Invited!
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            <span className="font-medium">{invitation.inviter_name}</span> has invited you to join
          </p>
        </div>

        {/* Target Info */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              isHub ? 'bg-purple-500' : 'bg-blue-500'
            }`}>
              <span className="text-white font-bold text-lg">
                {invitation.target_name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">
                {invitation.target_name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                {invitation.target_type} • {invitation.role} access
              </p>
            </div>
          </div>
        </div>

        {/* Personal Message */}
        {invitation.message && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-700 dark:text-blue-300 italic">
              "{invitation.message}"
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {isAuthenticated ? (
            <Button 
              onClick={handleAccept} 
              disabled={accepting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="accept-invite-btn"
            >
              {accepting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => navigate(`/register?invite=${inviteToken}`)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="signup-to-accept-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Sign Up to Accept
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate(`/login?invite=${inviteToken}`)}
                className="w-full"
                data-testid="login-to-accept-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Already have an account? Log In
              </Button>
            </>
          )}
        </div>

        {/* Expiration Notice */}
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
          This invitation expires on {new Date(invitation.expires_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default JoinPage;
